import { ChakraProvider } from '@chakra-ui/react';
import { ThemeProvider } from 'next-themes'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { retryUnlessThrottled } from '../lib/retryPolicy'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { system } from '../theme/theme'
import { Protocol } from 'pmtiles'
import maplibregl from 'maplibre-gl'
import { getTileUpstream } from '../lib/mapConfig'
import { registerMapCacheProtocol } from '../lib/mapCacheProtocol'
import { CommitteeScopeProvider } from '../contexts/CommitteeScopeContext'
import UsabilityOverlay from '../tools/usability/components/UsabilityOverlay'
import { Toaster } from '../components/ui/toaster'
import { PreferenceEffects } from '../preferences/PreferenceEffects'

// Register PMTiles protocol for self-hosted vector tiles
const protocol = new Protocol()
maplibregl.addProtocol('pmtiles', protocol.tile)

// Every basemap tile/glyph/sprite (getMapStyle()) reads through mapcache://: offline cache first,
// then whichever tile source this instance is configured with — resolved per request, because on
// native the instance's settings arrive after this module runs (lib/runtimeConfig.ts).
registerMapCacheProtocol(getTileUpstream)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: retryUnlessThrottled,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

interface AppProvidersProps {
  children: React.ReactNode
}

export function AppProviders({ children }: AppProvidersProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <CommitteeScopeProvider>
        <ChakraProvider value={system}>
          <ThemeProvider
            attribute="class"
            defaultTheme="system"
            enableSystem
            storageKey="pref:appearance.theme"
          >
            <PreferenceEffects />
            {children}
            {import.meta.env.DEV &&
              new URLSearchParams(window.location.search).has('usability') && <UsabilityOverlay />}
            <ReactQueryDevtools initialIsOpen={false} />
            <Toaster />
          </ThemeProvider>
        </ChakraProvider>
      </CommitteeScopeProvider>
    </QueryClientProvider>
  )
}
